import React, { useContext, useEffect, useState } from "react";

import ApplicationContext from "../../../context/ApplicationContext";
import { useNavigate } from "react-router-dom";
import {
  deleteSnookerTables,
  getSnookerTables,
} from "../../../services/Service";
import { generateGUID } from "../../../helpers/Helpers";
import {
  defaultSnookerTable,
  ISnookerTable,
} from "../../../types/ISnookerTable";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  FormTextArea,
  Icon,
  Label,
  Menu,
  MenuItem,
  Message,
  MessageHeader,
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";

export interface IAllProductsProps {
  editId(id: number): void;
}

export const AllTables: React.FC = (): JSX.Element => {
  const context = useContext(ApplicationContext);
  const navigate = useNavigate();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState("");
  const [error, setError] = useState(false);

  async function getSnookerSettings() {
    setLoading(true);
    const response = await getSnookerTables(
      context.AuthenticatedUser.getToken()
    );

    if (!response) {
      context.ApplicationError.setHasError(true);
      return;
    }

    context.Snooker.setData(response);

    setRefresh(generateGUID());
    setLoading(false);
  }

  useEffect(() => {
    getSnookerSettings();
  }, []);

  function confirmDeleteTable(st: ISnookerTable) {
    setError(false);
    context.Snooker.setSelected(st);
    setShowDeleteModal(true);
  }

  async function addTable() {
    navigate("/SnookerTableEditor/");
  }

  async function deleteTable() {
    setLoading(true);

    const response = await deleteSnookerTables(
      context.AuthenticatedUser.getToken(),
      context.Snooker.getSelected().uuid
    );

    if (!response) {
      setError(true);
      return;
    }

    getSnookerSettings();
    setShowDeleteModal(false);
    setLoading(false);
    setError(false);
  }

  function editTable(st: ISnookerTable) {
    navigate("/SnookerTableEditor/" + st.uuid);
  }

  const errorInComms = (): JSX.Element => {
    return (
      <Message negative>
        <MessageHeader>Network error</MessageHeader>
        <p>
          Error communicating with our services. Please try again. If the error
          persist please contact support{" "}
        </p>
      </Message>
    );
  };

  const deleteModal = (): JSX.Element => {
    return (
      <>
        <Modal open={showDeleteModal}>
          <ModalHeader>Delete:</ModalHeader>
          <ModalContent image scrolling>
            <ModalDescription>
              <div>{error ? errorInComms() : <></>}</div>
              <br />
              <p>Are you sure?</p>
            </ModalDescription>
          </ModalContent>
          <ModalActions>
            <Button onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button onClick={() => deleteTable()} negative>
              Delete
            </Button>
          </ModalActions>
        </Modal>
      </>
    );
  };

  function formatDateForDisplay(d: Date) {
    const dt = new Date(d);
    return dt.getHours() + ":" + dt.getMinutes();
  }

  const tableRow = (st: ISnookerTable) => (
    <>
      <TableRow>
        <TableCell>{st.position}</TableCell>
        <TableCell>{st.description}</TableCell>
        <TableCell>{st.peekPrice.toFixed(2)}</TableCell>
        <TableCell>{st.offPeekPrice.toFixed(2)}</TableCell>
        <TableCell>{formatDateForDisplay(st.peekStart)}</TableCell>
        <TableCell>{formatDateForDisplay(st.peekEnd)}</TableCell>
        <TableCell>
          <textarea style={{ width: 400, height: 120 }} value={st.style} />
        </TableCell>
        <TableCell>
          <div style={{ cursor: "pointer" }} onClick={() => editTable(st)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-file-diff"
              viewBox="0 0 16 16"
            >
              <path d="M8 4a.5.5 0 0 1 .5.5V6H10a.5.5 0 0 1 0 1H8.5v1.5a.5.5 0 0 1-1 0V7H6a.5.5 0 0 1 0-1h1.5V4.5A.5.5 0 0 1 8 4m-2.5 6.5A.5.5 0 0 1 6 10h4a.5.5 0 0 1 0 1H6a.5.5 0 0 1-.5-.5" />
              <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zm10-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1" />
            </svg>
          </div>
        </TableCell>
        <TableCell>
          <div
            style={{ cursor: "pointer" }}
            onClick={() => confirmDeleteTable(st)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-file-earmark-x"
              viewBox="0 0 16 16"
            >
              <path d="M6.854 7.146a.5.5 0 1 0-.708.708L7.293 9l-1.147 1.146a.5.5 0 0 0 .708.708L8 9.707l1.146 1.147a.5.5 0 0 0 .708-.708L8.707 9l1.147-1.146a.5.5 0 0 0-.708-.708L8 8.293z" />
              <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z" />
            </svg>
          </div>
        </TableCell>
      </TableRow>
    </>
  );

  const tablesView = (): JSX.Element => (
    <>
      <Menu attached="top">
        <Dropdown item simple text="Menu">
          <DropdownMenu>
            <DropdownItem onClick={() => addTable()}>Add</DropdownItem>
            <DropdownItem onClick={() => addTable()}>Edit</DropdownItem>
            <DropdownItem onClick={() => addTable()}>Delete</DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </Menu>

      <br />
      <div>
        <Table celled>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Position</TableHeaderCell>
              <TableHeaderCell>Description</TableHeaderCell>
              <TableHeaderCell>Peek Price</TableHeaderCell>
              <TableHeaderCell>Off Peek Price</TableHeaderCell>
              <TableHeaderCell>Peek Start</TableHeaderCell>
              <TableHeaderCell>Peek End</TableHeaderCell>
              <TableHeaderCell>Style</TableHeaderCell>
              <TableHeaderCell></TableHeaderCell>
              <TableHeaderCell></TableHeaderCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            {context.Snooker.getData().map((data) => tableRow(data))}
          </TableBody>
        </Table>
      </div>
    </>
  );

  return (
    <>
      <>{deleteModal()}</>
      <>{tablesView()}</>
    </>
  );
};
